import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileSignature, Lock, KeyRound, Database, Trash2, ClipboardCheck, Eye } from "lucide-react";
import { useSEO } from "@/lib/seo";
import CTASection from "@/components/CTASection";

const ITEMS = [
  { icon: ShieldCheck, t: "Confidentiality", d: "Client information and facility data are treated as confidential and used only to deliver the engagement you request." },
  { icon: FileSignature, t: "NDA availability", d: "We are glad to sign a mutual non-disclosure agreement before any information is shared. Request an NDA at the start of your engagement." },
  { icon: Lock, t: "Secure document handling", d: "Documents you upload are transmitted over encrypted (HTTPS) connections and stored in access-controlled cloud storage. Files are served only through our authenticated application — never from public links." },
  { icon: KeyRound, t: "Access controls", d: "Access to client files and lead data is restricted to authenticated team members through our admin application. Public visitors cannot browse or list uploaded documents." },
  { icon: Database, t: "Data handling", d: "We collect only the information needed to scope and deliver your work — facility details, chemical data and supporting documents. We do not sell client data." },
  { icon: Trash2, t: "Retention & deletion", d: "We retain project information for as long as needed to deliver and support your engagement. On written request, we will delete client documents that are no longer required." },
  { icon: ClipboardCheck, t: "Quality assurance", d: "Deliverables move through a structured, multi-step preparation and review workflow before they reach you." },
  { icon: Eye, t: "Technical review", d: "A separate review step checks calculations, chemical identification and documentation for consistency prior to delivery." },
];

export default function TrustSecurity() {
  useSEO({
    title: "Trust & Security",
    description:
      "How Wasserlab — EHS Remote handles confidentiality, NDAs, secure document handling, access controls, data retention and quality assurance for U.S. environmental compliance work.",
    path: "/trust-security",
  });

  return (
    <>
      <section className="relative overflow-hidden bg-navy-900 noise-overlay">
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Trust &amp; Security</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            Built for confidential environmental and EHS work
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">
            Industrial clients share sensitive facility and chemical information. Here is how we handle it — described
            honestly, with no claims we cannot stand behind.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {ITEMS.map((it, i) => {
              const Icon = it.icon;
              return (
                <div key={i} className="flex items-start gap-4 rounded-xl border border-slate-200 p-6" data-testid={`trust-item-${i}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h2 className="font-heading text-base font-bold text-navy-900">{it.t}</h2>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{it.d}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 rounded-xl border border-amber-200 bg-amber-50 p-6">
            <p className="text-sm leading-relaxed text-slate-700">
              We describe our practices in plain language and do not claim specific security certifications or formal
              compliance standards (such as SOC 2 or ISO 27001) that we have not obtained. If your organization requires
              a specific data-handling arrangement, tell us and we will confirm what we can support before you share any
              information.
            </p>
          </div>
        </div>
      </section>
      <CTASection heading="Ready to discuss a confidential engagement?" sub="Request an NDA and a scoped assessment — no obligation." location="trust-footer" />
    </>
  );
}
